import React, { useContext, useEffect, useRef } from "react";
import { groupBy, sortBy } from "lodash";
import { RESOURCE_NAME } from "common/authorization";
import { COMMENT_OBJECT_TYPE } from "common/constants";
import { useGetCommentsForObjectQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { formatDate } from "../ActivityFeed/dateHelper";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { Body } from "../Common/Typography";
import { Wrapper } from "./__styles__/Thread";
import { Comment } from "./Comment";
import { CreateComment } from "./CreateComment";

export const Thread = ({
  objectId,
  objectType,
}: {
  objectId: string;
  objectType: COMMENT_OBJECT_TYPE;
}) => {
  const { authorized } = useContext(AuthContext);
  const { data, refetch } = useGetCommentsForObjectQuery({
    variables: {
      objectId,
      objectType,
    },
  });

  const comments = data?.getCommentsForObject || [];
  const sortedComments = sortBy(comments, "createdAt");
  const groupedCommentsByDay = groupBy(sortedComments, comment =>
    formatDate({ dateString: comment.createdAt }),
  );
  const mostRecentCommentId = sortedComments[sortedComments.length - 1]?.id;
  const mostRecentCommentRef = useRef<HTMLDivElement>(null);

  const canCreateComment = authorized({
    resource: RESOURCE_NAME.COMMENT,
    permission: "create",
  });

  // Scroll to the most recent comment
  useEffect(() => {
    if (!mostRecentCommentId || !mostRecentCommentRef.current) return;

    // Small delay to ensure the comment is fully rendered
    const timeoutId = setTimeout(() => {
      mostRecentCommentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [mostRecentCommentId]);

  return (
    <Wrapper>
      {Object.entries(groupedCommentsByDay).map(([day, comments]) => (
        <FlexColumn key={day} style={{ gap: `${spacing.s}` }}>
          <Body size="small" type="emphasis">
            {day}
          </Body>
          <div>
            {comments.map(comment => {
              const isMostRecent = comment.id === mostRecentCommentId;
              return (
                <div
                  key={comment.id}
                  ref={isMostRecent ? mostRecentCommentRef : undefined}
                >
                  <Comment comment={comment} />
                </div>
              );
            })}
          </div>
        </FlexColumn>
      ))}
      {canCreateComment && (
        <CreateComment
          refetchComments={refetch}
          objectId={objectId}
          objectType={objectType}
        />
      )}
    </Wrapper>
  );
};
